import { useState, useEffect } from "react";
import {
  AlertCircle,
  Clock,
  DollarSign,
  User,
  Calendar,
  Phone,
  Mail,
  ChevronRight,
} from "lucide-react";
import medicalDB from "../services/database";

const OutstandingPayments = ({ limit = null, showHeader = true }) => {
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [sortBy, setSortBy] = useState("amount"); // amount, date, patient
  const [filterBy, setFilterBy] = useState("all"); // all, partial, overdue

  useEffect(() => {
    loadOutstandingPayments();
  }, [sortBy, filterBy]);

  const loadOutstandingPayments = async () => {
    try {
      setLoading(true);

      // Get all transactions with outstanding balances
      const allTransactions = await medicalDB.getAllTransactions();

      // Filter for transactions with outstanding amounts
      let outstanding = allTransactions.filter((transaction) => {
        const balance = transaction.totalAmount - transaction.paidAmount;
        return balance > 0;
      });

      // Apply status filter
      if (filterBy !== "all") {
        outstanding = outstanding.filter((transaction) => {
          if (filterBy === "partial") {
            return (
              transaction.status ===
              (medicalDB.PAYMENT_STATUS?.PARTIAL || "partial")
            );
          } else if (filterBy === "overdue") {
            // Consider overdue if unpaid for more than 30 days
            const daysSinceTransaction = Math.floor(
              (new Date() - new Date(transaction.date)) / (1000 * 60 * 60 * 24)
            );
            return daysSinceTransaction > 30;
          }
          return true;
        });
      }

      // Calculate additional data for each transaction
      outstanding = outstanding.map((transaction) => {
        const balance = transaction.totalAmount - transaction.paidAmount;
        const daysSinceTransaction = Math.floor(
          (new Date() - new Date(transaction.date)) / (1000 * 60 * 60 * 24)
        );

        let urgencyLevel = "low";
        if (daysSinceTransaction > 60) urgencyLevel = "high";
        else if (daysSinceTransaction > 30) urgencyLevel = "medium";

        return {
          ...transaction,
          balance,
          daysSinceTransaction,
          urgencyLevel,
        };
      });

      // Sort the results
      outstanding.sort((a, b) => {
        switch (sortBy) {
          case "amount":
            return b.balance - a.balance;
          case "date":
            return new Date(a.date) - new Date(b.date); // Oldest first
          case "patient":
            return (a.patient?.name || "").localeCompare(b.patient?.name || "");
          default:
            return b.balance - a.balance;
        }
      });

      // Apply limit if specified
      if (limit) {
        outstanding = outstanding.slice(0, limit);
      }

      // Calculate total outstanding
      const total = outstanding.reduce(
        (sum, transaction) => sum + transaction.balance,
        0
      );

      setOutstandingPayments(outstanding);
      setTotalOutstanding(total);
    } catch (error) {
      console.error("Error loading outstanding payments:", error);
      setOutstandingPayments([]);
      setTotalOutstanding(0);
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgencyLevel) => {
    switch (urgencyLevel) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getUrgencyIcon = (urgencyLevel) => {
    switch (urgencyLevel) {
      case "high":
        return <AlertCircle className="h-4 w-4" />;
      case "medium":
        return <Clock className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount) => `₪${amount.toFixed(2)}`;

  const handleContactPatient = (patient) => {
    // This could be extended to integrate with email/SMS services
    if (patient.phone) {
      window.open(`tel:${patient.phone}`);
    } else if (patient.email) {
      window.open(`mailto:${patient.email}`);
    } else {
      alert(`Contact information not available for ${patient.name}`);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">
            Loading outstanding payments...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {showHeader && (
        <>
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Outstanding Payments
                  </h3>
                  <p className="text-sm text-gray-600">
                    {outstandingPayments.length} unpaid transactions
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(totalOutstanding)}
                </div>
                <div className="text-sm text-gray-600">Total Outstanding</div>
              </div>
            </div>
          </div>

          {/* Filters and Sort */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Status
                </label>
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Outstanding</option>
                  <option value="partial">Partially Paid</option>
                  <option value="overdue">Overdue (30+ days)</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort by
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="amount">Amount (High to Low)</option>
                  <option value="date">Date (Oldest First)</option>
                  <option value="patient">Patient Name</option>
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Outstanding Payments List */}
      <div className="divide-y divide-gray-200">
        {outstandingPayments.length === 0 ? (
          <div className="p-8 text-center">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Outstanding Payments
            </h3>
            <p className="text-gray-600">
              All transactions have been paid in full.
            </p>
          </div>
        ) : (
          outstandingPayments.map((transaction) => (
            <div
              key={transaction.id}
              className="p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getUrgencyColor(
                        transaction.urgencyLevel
                      )}`}
                    >
                      {getUrgencyIcon(transaction.urgencyLevel)}
                      <span className="capitalize">
                        {transaction.urgencyLevel}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      #{transaction.receiptNumber}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-1">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {transaction.patient?.name || "Unknown Patient"}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {new Date(transaction.date).toLocaleDateString("en-IL")}
                      </span>
                    </div>
                    <span>•</span>
                    <span>{transaction.daysSinceTransaction} days ago</span>
                  </div>

                  <div className="mt-2 text-sm text-gray-600">
                    Total: {formatCurrency(transaction.totalAmount)} • Paid:{" "}
                    {formatCurrency(transaction.paidAmount)}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-600">
                      {formatCurrency(transaction.balance)}
                    </div>
                    <div className="text-sm text-gray-500">Outstanding</div>
                  </div>

                  {transaction.patient && (
                    <button
                      onClick={() => handleContactPatient(transaction.patient)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Contact Patient"
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                  )}

                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Summary */}
      {!showHeader && outstandingPayments.length > 0 && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">
              {outstandingPayments.length} outstanding transactions
            </span>
            <span className="font-semibold text-red-600">
              Total: {formatCurrency(totalOutstanding)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutstandingPayments;
